/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createLogger } from '@/lib/logger';

const logger = createLogger();

export const LIVE_PERSON_ENGAGEMENT_ID = 'lp-chat-icon';
const MAX_SKILL_LENGTH = 128;
const DELIVERY_ZIP_PATTERN = /^\d{5}(?:-\d{4})?$/;

export type LivePersonCustomerInfoSde = {
    type: 'ctmrinfo';
    info: {
        ctype: string;
        storeZipCode: string;
    };
};

export type LivePersonCartSde = {
    type: 'Live Person Tracking';
    total: number;
    currency: 'USD';
    numItems: number;
    products: string[];
};

export type LivePersonSde = LivePersonCustomerInfoSde | LivePersonCartSde;

export interface LivePersonTag {
    sdes?: {
        push(sde: LivePersonSde): unknown;
    };
    taglets?: {
        lp_monitoringSDK?: {
            getVid(): string | null | undefined | Promise<string | null | undefined>;
        };
    };
}

declare global {
    interface Window {
        lpTag?: LivePersonTag;
    }
}

/** Pushes Ashley delivery ZIP and skill data to the externally established tag. */
export function pushLivePersonCustomerInfo(tag: LivePersonTag, skill: string, deliveryZip: string): boolean {
    const ctype = skill.trim();
    const storeZipCode = deliveryZip.trim();
    if (!ctype || ctype.length > MAX_SKILL_LENGTH) {
        logger.warn('[LivePerson] customer-info SDE skipped because the configured skill is invalid');
        return false;
    }
    if (!DELIVERY_ZIP_PATTERN.test(storeZipCode)) {
        logger.warn('[LivePerson] customer-info SDE skipped because the delivery ZIP is invalid');
        return false;
    }

    if (!tag.sdes || typeof tag.sdes.push !== 'function') return false;
    tag.sdes.push({
        type: 'ctmrinfo',
        info: {
            ctype,
            storeZipCode,
        },
    });
    return true;
}

/** Pushes the Ashley Next cart SDE to the externally established tag. */
export function pushLivePersonCart(
    tag: LivePersonTag,
    { total, numItems, products }: Omit<LivePersonCartSde, 'type' | 'currency'>
): boolean {
    if (!Number.isFinite(total) || numItems < 0 || !tag.sdes || typeof tag.sdes.push !== 'function') return false;

    tag.sdes.push({
        type: 'Live Person Tracking',
        total,
        currency: 'USD',
        numItems,
        products,
    });
    return true;
}
